import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Trash2, Shield } from 'lucide-react';

interface UserRole {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  email?: string;
}

type DbUserRole = {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
};

export default function UserRolesManager() {
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<string>('admin');

  useEffect(() => {
    loadUserRoles();
  }, []);

  const loadUserRoles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const typedData = (data || []) as DbUserRole[];

      // Fetch user emails
      if (typedData.length > 0) {
        const { data: usersData, error: usersError } = await supabase.auth.admin.listUsers();
        
        const users = (usersData?.users || []) as Array<{ id: string; email?: string }>;
        
        if (!usersError && users.length > 0) {
          const rolesWithEmails: UserRole[] = typedData.map(role => ({
            id: role.id,
            user_id: role.user_id,
            role: role.role,
            created_at: role.created_at,
            email: users.find(u => u.id === role.user_id)?.email || 'Unknown'
          }));
          setUserRoles(rolesWithEmails);
        } else {
          const rolesWithUnknownEmail: UserRole[] = typedData.map(role => ({
            id: role.id,
            user_id: role.user_id,
            role: role.role,
            created_at: role.created_at,
            email: 'Unknown'
          }));
          setUserRoles(rolesWithUnknownEmail);
        }
      } else {
        setUserRoles([]);
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
      toast.error('Failed to load user roles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!newUserEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      // Find user by email
      const { data: usersData, error: usersError } = await supabase.auth.admin.listUsers();
      if (usersError) throw usersError;

      const users = (usersData?.users || []) as Array<{ id: string; email?: string }>;
      const user = users.find(u => u.email?.toLowerCase() === newUserEmail.toLowerCase());
      if (!user) {
        toast.error('User not found with that email');
        return;
      }

      // Add role
      const { error } = await supabase
        .from('user_roles')
        .insert([{ user_id: user.id, role: newUserRole as any }]);

      if (error) {
        if (error.code === '23505') {
          toast.error('User already has this role');
        } else {
          throw error;
        }
        return;
      }

      toast.success(`${newUserRole} role granted to ${newUserEmail}`);
      setNewUserEmail('');
      setNewUserRole('admin');
      loadUserRoles();
    } catch (error) {
      console.error('Error adding role:', error);
      toast.error('Failed to add role');
    }
  };

  const handleDeleteRole = async (roleId: string, email?: string) => {
    if (!confirm(`Remove this role from ${email || 'this user'}?`)) return;

    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;

      toast.success('Role removed successfully');
      loadUserRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error('Failed to remove role');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          User Roles Management
        </h2>
        <p className="text-muted-foreground mt-1">
          Grant admin, premium, or free roles to users. Admin users have full access without payment.
        </p>
      </div>

      {/* Add Role Form */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="User email address"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            className="flex-1"
          />
          <Select value={newUserRole} onValueChange={setNewUserRole}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
              <SelectItem value="free">Free</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleAddRole} className="sm:w-auto">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Role
          </Button>
        </div>
      </Card>

      {/* Roles Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Granted</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {userRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No user roles found
                </TableCell>
              </TableRow>
            ) : (
              userRoles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell className="font-medium">{role.email}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      role.role === 'admin' ? 'bg-primary/20 text-primary' :
                      role.role === 'premium' ? 'bg-accent/20 text-accent-foreground' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {role.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(role.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRole(role.id, role.email)}
                      className="hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}